import React, { createContext, useContext, useCallback, useEffect, useMemo, useState } from 'react';

const ThemeContext = createContext(null);

const THEME_STORAGE_KEY = 'theme-preference';
const FALLBACK_THEME = 'system';
const isBrowser = typeof window !== 'undefined';

const readStoredTheme = () => {
  if (!isBrowser) {
    return FALLBACK_THEME;
  }

  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
    return FALLBACK_THEME;
  } catch (error) {
    console.warn('Unable to access localStorage for theme preference:', error);
    return FALLBACK_THEME;
  }
};

const getSystemTheme = () => {
  if (!isBrowser) {
    return 'light';
  }
  return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(readStoredTheme);
  const [resolvedTheme, setResolvedTheme] = useState(() => {
    const stored = readStoredTheme();
    return stored === 'system' ? getSystemTheme() : stored;
  });

  const applyResolvedTheme = useCallback((nextTheme) => {
    if (!isBrowser) {
      return;
    }
    document.documentElement.setAttribute('data-theme', nextTheme);
    document.documentElement.style.colorScheme = nextTheme;
    setResolvedTheme(nextTheme);
  }, []);

  useEffect(() => {
    if (!isBrowser) {
      return;
    }

    const activeTheme = theme === 'system' ? getSystemTheme() : theme;
    applyResolvedTheme(activeTheme);

    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch (error) {
      console.warn('Unable to persist theme preference:', error);
    }
  }, [applyResolvedTheme, theme]);

  useEffect(() => {
    if (!isBrowser || theme !== 'system') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (event) => {
      applyResolvedTheme(event.matches ? 'dark' : 'light');
    };

    handleChange(mediaQuery);
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [applyResolvedTheme, theme]);

  const setTheme = useCallback((nextTheme) => {
    setThemeState(nextTheme);
  }, []);

  const value = useMemo(
    () => ({
      theme,
      resolvedTheme,
      setTheme,
    }),
    [theme, resolvedTheme, setTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
